import { useEffect, useRef, useState } from "react";
import { api, fileUrl, wsUrl } from "../lib/api";
import { toast } from "sonner";
import { Send, UserPlus, X, Check } from "lucide-react";

const DEFAULT_TOKEN = "https://images.unsplash.com/photo-1766941234616-ba978cb000e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxyZXRybyUyMGNvbXB1dGVyJTIwdGVybWluYWx8ZW58MHx8fHwxNzc3NTk4MDkyfDA&ixlib=rb-4.1.0&q=85";

export default function Friends() {
  const [myChars, setMyChars] = useState([]);
  const [selfCid, setSelfCid] = useState("");
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [allChars, setAllChars] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [activeChat, setActiveChat] = useState(null);
  const [chatMode, setChatMode] = useState("dm"); // "dm" | "gm-dm"
  const [gmThreads, setGmThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const chatLogRef = useRef(null);
  const scrollToBottom = () => requestAnimationFrame(() => {
    if (chatLogRef.current) chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
  });

  const loadMine = async () => {
    try {
      const r = await api.get("/characters");
      const mine = r.data.filter(c => c.owner_id && (!selfCid ? true : true));
      setMyChars(r.data); // may include GM-visible, but we filter in UI
    } catch {}
  };
  useEffect(() => { loadMine(); }, []);

  const loadFriends = async (cid) => {
    if (!cid) return;
    try {
      const [fr, cands] = await Promise.all([
        api.get(`/characters/${cid}/friends`),
        api.get(`/characters/${cid}/friend-candidates`),
      ]);
      setFriends(fr.data.friends);
      setRequests(fr.data.requests);
      setAllChars(cands.data);
    } catch { toast.error("Load failed"); }
  };

  useEffect(() => { loadFriends(selfCid); setActiveChat(null); wsRef.current?.close(); }, [selfCid]);

  const openChat = async (otherCid) => {
    setActiveChat(otherCid); setChatMode("dm");
    try { const r = await api.get(`/chat/dm/${selfCid}/${otherCid}`); setMessages(r.data); } catch { setMessages([]); }
    wsRef.current?.close();
    const ws = new WebSocket(wsUrl("dm", `${selfCid}/${otherCid}`));
    ws.onmessage = (ev) => setMessages(prev => [...prev, JSON.parse(ev.data)]);
    wsRef.current = ws;
  };
  const openGmDm = async (charId) => {
    setActiveChat(charId); setChatMode("gm-dm");
    try { const r = await api.get(`/chat/gm-dm/${charId}`); setMessages(r.data); } catch { setMessages([]); }
    wsRef.current?.close();
    const ws = new WebSocket(wsUrl("gm-dm", charId));
    ws.onmessage = (ev) => setMessages(prev => [...prev, JSON.parse(ev.data)]);
    wsRef.current = ws;
  };
  useEffect(() => () => wsRef.current?.close(), []);
  useEffect(() => { scrollToBottom(); }, [messages, activeChat]);

  // Load GM-DM threads on mount and after self-character changes
  const loadGmThreads = async () => {
    try { const r = await api.get("/chat/gm-dm-threads"); setGmThreads(r.data); } catch {}
  };
  useEffect(() => { loadGmThreads(); }, []);

  // Deep link: /friends?gm-char=<cid>
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const gmCid = p.get("gm-char");
    if (gmCid) openGmDm(gmCid);
    // eslint-disable-next-line
  }, []);

  const send = () => {
    if (!text.trim() || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ content: text }));
    setText("");
  };

  const request = async (targetCid) => {
    try { await api.post(`/characters/${selfCid}/friend-request/${targetCid}`); toast.success("Request sent"); loadFriends(selfCid); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const accept = async (requesterCid) => {
    try { await api.post(`/characters/${selfCid}/friend-accept/${requesterCid}`); toast.success("Accepted"); loadFriends(selfCid); }
    catch { toast.error("Failed"); }
  };
  const removeFriend = async (otherCid) => {
    try { await api.post(`/characters/${selfCid}/friend-remove/${otherCid}`); toast.success("Removed"); if (activeChat===otherCid) setActiveChat(null); loadFriends(selfCid); }
    catch {}
  };

  const ownChars = myChars; // they're all visible characters; self-selection picks from own
  const candidates = allChars.filter(c => c.id !== selfCid && !friends.some(f=>f.id===c.id) && !requests.some(r=>r.id===c.id));
  const filteredCandidates = candidates.filter(c => c.name.toLowerCase().includes(searchName.toLowerCase()));
  const otherChar = friends.find(f => f.id === activeChat);

  return (
    <div className="p-4 sm:p-8">
      <div>
        <h1 className="heading-mono text-3xl sm:text-4xl amber amber-glow uppercase">// Comms</h1>
        <div className="mono text-xs text-[#A0A0A0] mt-1 tracking-widest uppercase">// Where plans are made and ignored <br/><br/></div>
    </div>
      <div className="mb-6 panel p-4">
        <label className="label-term mb-2 block">Acting as character</label>
        <select className="input-term max-w-md" value={selfCid} onChange={e=>setSelfCid(e.target.value)} data-testid="friends-self-select">
          <option value="">// select your character</option>
          {ownChars.map(c => <option key={c.id} value={c.id}>{c.name} — {c.career || "—"}</option>)}
        </select>
      </div>

      {selfCid ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="space-y-4">
            <div className="panel p-4">
              <div className="label-term amber mb-3">// Pending Requests ({requests.length})</div>
              {requests.length === 0 && <div className="mono text-xs text-[#A0A0A0]">// none</div>}
              {requests.map(r => (
                <div key={r.id} className="flex justify-between items-center border-b border-[#222] py-2">
                  <span className="amber mono truncate">{r.name}</span>
                  <button className="btn-amber text-xs flex items-center gap-1" onClick={()=>accept(r.id)} data-testid={`accept-${r.id}`}><Check size={12}/> Accept</button>
                </div>
              ))}
            </div>
            <div className="panel p-4">
              <div className="label-term amber mb-3">// Friends ({friends.length})</div>
              {friends.length === 0 && <div className="mono text-xs text-[#A0A0A0]">// no friends</div>}
              {friends.map(f => (
                <div key={f.id} className="flex items-center justify-between border-b border-[#222] py-2">
                  <button className="flex items-center gap-2 hover:underline" onClick={()=>openChat(f.id)} data-testid={`open-dm-${f.id}`}>
                    <img src={f.token_path ? fileUrl(f.token_path) : DEFAULT_TOKEN} alt="" className="w-8 h-8 rounded-full token-img object-cover" />
                    <span className="amber mono">{f.name}</span>
                  </button>
                  <button className="text-[#FF2A2A]" onClick={()=>removeFriend(f.id)}><X size={14}/></button>
                </div>
              ))}
            </div>
            <div className="panel p-4">
              <div className="label-term amber mb-3">// Find Characters</div>
              <input className="input-term mb-2" placeholder="search by name..." value={searchName} onChange={e=>setSearchName(e.target.value)} data-testid="friend-search" />
              <div className="max-h-48 overflow-y-auto">
                {filteredCandidates.slice(0,30).map(c => (
                  <div key={c.id} className="flex items-center justify-between border-b border-[#222] py-2">
                    <span className="mono">{c.name}</span>
                    <button className="btn-amber text-xs flex items-center gap-1" onClick={()=>request(c.id)} data-testid={`req-${c.id}`}><UserPlus size={12}/> Request</button>
                  </div>
                ))}
                {filteredCandidates.length === 0 && <div className="mono text-xs text-[#A0A0A0]">// no matches</div>}
              </div>
            </div>

            {gmThreads.length > 0 && (
              <div className="panel p-4" data-testid="gm-threads-panel">
                <div className="label-term amber mb-3">// GM Channels ({gmThreads.length})</div>
                {gmThreads.map(t => (
                  <button key={`${t.role}-${t.character_id}`} className="flex items-center justify-between w-full border-b border-[#222] py-2 hover:bg-[#1a1100]" onClick={()=>openGmDm(t.character_id)} data-testid={`open-gmdm-${t.character_id}`}>
                    <span className="mono text-left">
                      <span className="amber">{t.role==="gm" ? `GM → ${t.character_name}` : `GM whisper — ${t.character_name}`}</span>
                      <span className="block text-[10px] text-[#A0A0A0]">{t.campaign_name}</span>
                    </span>
                    <span className="mono text-[9px] text-[#A0A0A0] uppercase">{t.role}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 panel p-4 flex flex-col h-[600px]" data-testid="dm-panel">
            {activeChat && chatMode === "gm-dm" ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-full border border-[#FFB000] flex items-center justify-center amber heading-mono">GM</div>
                  <div>
                    <div className="amber mono">GM Channel</div>
                    <div className="mono text-[10px] text-[#A0A0A0]">private to {(gmThreads.find(t=>t.character_id===activeChat)?.character_name) || activeChat}</div>
                  </div>
                </div>
                <div ref={chatLogRef} className="chat-log flex-1 overflow-y-auto p-3 border border-[#FFB000] amber">
                  {messages.map(m => (
                    <div key={m.id} className="mb-1">
                      <span className="text-[#A0A0A0] mr-2">[{new Date(m.created_at).toLocaleTimeString()}]</span>
                      <span className={m.from_role==="gm" ? "neon-green" : "amber"}>{m.username}:</span> <span style={{whiteSpace:"pre-wrap"}}>{m.content}</span>
                    </div>
                  ))}
                  {messages.length === 0 && <div className="text-[#555]">// no whispers yet</div>}
                </div>
                <div className="flex gap-2 mt-3">
                  <input className="input-term flex-1" placeholder="> private whisper..." value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} data-testid="gmdm-input" />
                  <button className="btn-amber" onClick={send} data-testid="gmdm-send"><Send size={14}/></button>
                </div>
              </>
            ) : activeChat && otherChar ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <img src={otherChar.token_path ? fileUrl(otherChar.token_path) : DEFAULT_TOKEN} alt="" className="w-10 h-10 rounded-full token-img object-cover" />
                  <div>
                    <div className="amber mono">{otherChar.name}</div>
                    <div className="mono text-[10px] text-[#A0A0A0]">{otherChar.career}</div>
                  </div>
                </div>
                <div ref={chatLogRef} className="chat-log flex-1 overflow-y-auto p-3 border border-[#222] amber">
                  {messages.map(m => (
                    <div key={m.id} className="mb-1"><span className="text-[#A0A0A0] mr-2">[{new Date(m.created_at).toLocaleTimeString()}]</span><span className="amber">{m.username}:</span> {m.content}</div>
                  ))}
                  {messages.length === 0 && <div className="text-[#555]">// encrypted channel idle</div>}
                </div>
                <div className="flex gap-2 mt-3">
                  <input className="input-term flex-1" placeholder="> encrypted message..." value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} data-testid="dm-input" />
                  <button className="btn-amber" onClick={send} data-testid="dm-send"><Send size={14}/></button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center mono text-[#A0A0A0]">// Select a friend to establish channel</div>
            )}
          </div>
        </div>
      ) : (
        <div className="panel p-8 mono text-[#A0A0A0]">// Select a character above to view its friends and messages.</div>
      )}
    </div>
  );
}
